import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

function cartJsUrl() {
  const theme = /** @type {any} */ (globalThis).Theme;
  const shopify = /** @type {any} */ (globalThis).Shopify;
  if (theme && theme.routes && theme.routes.cart_url) {
    return `${theme.routes.cart_url}.js`;
  }
  const root = shopify && shopify.routes && shopify.routes.root ? shopify.routes.root : '/';
  return `${root.endsWith('/') ? root : `${root}/`}cart.js`;
}

/**
 * Mirrors `snippets/lsg-cart-visible-quantity.liquid`: count line rows, skip bundle parent shell lines.
 * @param {Array<{ properties?: Record<string, unknown> | null }> | undefined} items
 * @returns {number}
 */
function lsgVisibleCartLineCountFromItems(items) {
  if (!Array.isArray(items)) return 0;
  let count = 0;
  for (const item of items) {
    const p = item.properties;
    const parentRaw = p && typeof p === 'object' ? p.bundle_parent : undefined;
    const isParent = parentRaw === true || parentRaw === 'true';
    if (isParent) continue;
    count += 1;
  }
  return count;
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * @param {number} count
   */
  #updateScreenReaderCount(count) {
    const screenReaderCount = this.refs.cartBubbleText?.querySelector('.visually-hidden');
    if (!screenReaderCount) return;
    const label =
      (screenReaderCount.textContent || '').split(':')[0]?.trim() || 'Total items in cart';
    screenReaderCount.textContent = `${label}: ${count}`;
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);

    this.currentCartCount = nextCount;
    this.#updateScreenReaderCount(nextCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      }),
    );

    if (!animate || nextCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    fetch(cartJsUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then((cart) => {
        this.renderCartBubble(lsgVisibleCartLineCountFromItems(cart.items), false, false);
      })
      .catch(() => {
        const sessionStorageCount = sessionStorage.getItem('cart-count');
        if (sessionStorageCount === null) return;

        const visibleCount = this.refs.cartBubbleCount.textContent;

        try {
          const { value, timestamp } = JSON.parse(sessionStorageCount);
          if (value === visibleCount) return;

          if (Date.now() - timestamp < 10000) {
            const count = parseInt(value, 10);

            if (count >= 0) {
              this.renderCartBubble(count, false, false);
            }
          }
        } catch (_) {
          // no-op
        }
      });
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
